import { useAuth } from '@/store/auth';
import { CONSTANT } from '@/store/constants';
import { formatDisplay } from '@/utils/format-display';
import { useMemo } from 'react';
import useSWR from 'swr';
import { useShallow } from 'zustand/shallow';

export const KEY_REF_CODE = `@/${CONSTANT.APP_ID}/ref-code`;
const KEY_REF_CODE_DONE = `@/refcode-done`;

export interface Stats {
  address: string;
  volume: number;
}

export interface DataStats {
  data: {
    refCount: number;
    totalVolume: number;
    stats: Stats[];
    refReward: number;
    totalReward: number;
    claimedReward: number;
  };
}

async function fetchData(url: string) {
  const res = await fetch(url);

  const data = await res.json();

  return data;
}

export function useRefCode() {
  const { address } = useAuth(
    useShallow((state) => ({
      address: state.address,
    }))
  );
  const res = useSWR<{
    user: string;
    refBy: string | null;
    refCode: string;
    createdAt: string;
  }>(
    address
      ? `${CONSTANT.API_EARN_URL}/api/user-ref?walletAddress=${address}`
      : null,
    fetchData,
    {
      dedupingInterval: 2000,
    }
  );

  return res;
}

export function useRefCodeStats() {
  const { address } = useAuth(
    useShallow((state) => ({
      address: state.address,
    }))
  );
  const res = useSWR<DataStats>(
    address
      ? `${CONSTANT.API_EARN_URL_FUTURE}/api/referral/trading-stats/${address}`
      : null,
    fetchData,
    {
      dedupingInterval: 2000,
    }
  );

  const data = useMemo(() => {
    if (!res.data) {
      return {
        data: {
          refCount: 0,
          totalVolume: 0,
          stats: [],
          refReward: 0,
          totalReward: 0,
          claimedReward: 0,
        },
      };
    }
    return {
      data: {
        refCount: res.data.data.refCount,
        totalVolume: formatDisplay(
          res.data.data.totalVolume / Math.pow(10, 18),
          {
            decimalToShow: 2,
          }
        ),
        stats: res.data.data.stats || [],
        refReward: formatDisplay(res.data.data.refReward / Math.pow(10, 18), {
          decimalToShow: 3,
        }),
        totalReward: formatDisplay(
          res.data.data.totalReward / Math.pow(10, 18),
          {
            decimalToShow: 3,
          }
        ),
        claimedReward: formatDisplay(
          res.data.data.claimedReward / Math.pow(10, 18),
          {
            decimalToShow: 3,
          }
        ),
      },
    };
  }, [res.data]);

  return {
    ...res,
    dataDisplay: data,
  };
}

export async function submitRefCode(data: {
  signature: string;
  address: string;
  message: string;
  refCode?: string | null;
}) {
  if (localStorage.getItem(KEY_REF_CODE_DONE)) {
    return false;
  }
  const refCode = localStorage.getItem(KEY_REF_CODE);
  const body = {
    ...data,
    refCode: data.refCode || refCode,
  };

  try {
    const res = await fetch(`${CONSTANT.API_EARN_URL}/api/user-ref`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      return false
    }
    localStorage.setItem(KEY_REF_CODE_DONE, '1');
    return true;
  } catch (error: any) {
    return false;
  }
}
